import { useEffect, useMemo, useState } from "react";
import { useGetProductById } from "../../../../api/product";
import { useGetProductSummary, useGetProductSummaryHistoryDropDownData } from "../../../../api/productSummary";
import { DISABLE_IS_ROOT_PRODUCT_BUTTON } from "../../../../config/constants";
import { IsRootProduct } from "../../../../config/enums/ProductType.enum";
import { ProductDetailsViewModel } from "../types/productDetailsView.types";

interface UseProductDetailsViewModelParams {
  productId: string;
  setVisibleModal: (visible: boolean) => void;
  navigateToEdit: () => void;
}

/**
 * Owns the page-level state the legacy `ProductDetailsModal` kept inline
 * (see src/screens/product/productDetails/ProductDetailsModal.tsx): which
 * tab is selected and which sales unit is selected — plus the queries that
 * drive the sales-unit dropdown and the top-level loading gate. Each tab
 * component still owns its own data-fetching for its own content (matching
 * the legacy split between this modal and its four `productViewTab/*`
 * children) rather than centralizing everything here — same boundary the
 * legacy modal already drew. The History tab is the extreme case of this:
 * it renders the shared `VersionHistoryPanel`, which owns its own pagination
 * entirely.
 */
export const useProductDetailsViewModel = ({
  productId,
  setVisibleModal,
  navigateToEdit
}: UseProductDetailsViewModelParams): ProductDetailsViewModel => {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [defaultSalesUnit, setDefaultSalesUnit] = useState<string>("");

  const { data: productDetails, isLoading: productDetailsLoading } = useGetProductById(productId);

  const productIsAverage = !DISABLE_IS_ROOT_PRODUCT_BUTTON
    ? productDetails?.is_average_product || false
    : false;
  const productIsAverageResult = productIsAverage ? IsRootProduct.YES : IsRootProduct.NO;

  // `productSummaryData` mirrors the legacy modal exactly: only its
  // `.isFetching` drives the top-level loading gate below — the payload
  // itself goes unused here too (the Environmental Footprint tab fetches
  // its own copy, same as the legacy `ProductImpactTab`).
  const productSummaryData = useGetProductSummary({
    productId,
    salesUnitId: defaultSalesUnit,
    productIsAverage
  });
  const {
    data: productSummaryDropdownData,
    isFetching: isProductSummaryHistoryLoading
  } = useGetProductSummaryHistoryDropDownData({
    productId,
    salesUnitId: defaultSalesUnit,
    productIsAverage
  });

  const salesUnitOptions = useMemo(() => {
    return (productDetails?.sales_units || []).map((unit: any) => ({
      value: unit?._id,
      text: unit?.sales_unit_name
    }));
  }, [productDetails]);

  const filteredSalesUnit = useMemo(() => {
    return productDetails?.sales_units?.filter((unit: any) => unit?._id === defaultSalesUnit) || [];
  }, [defaultSalesUnit, productDetails]);

  const selectedUnitSize = useMemo(() => {
    if (productDetails && defaultSalesUnit) {
      const selectedUnit = productDetails?.sales_units.find((unit: any) => unit._id === defaultSalesUnit);
      return parseFloat(selectedUnit?.sales_unit_size) || 1;
    }
    return 1;
  }, [productDetails, defaultSalesUnit]);

  const selectedUnitType = useMemo(() => {
    if (productDetails && defaultSalesUnit) {
      const selectedUnit = productDetails?.sales_units.find((unit: any) => unit._id === defaultSalesUnit);
      return selectedUnit?.sales_unit_type || "kg";
    }
    return "kg";
  }, [productDetails, defaultSalesUnit]);

  useEffect(() => {
    setDefaultSalesUnit(productDetails?.sales_units?.[0]?._id);
  }, [productDetails]);

  const onChangeSalesUnit = (value: string) => {
    setDefaultSalesUnit(value);
  };

  return {
    productDetails,
    productDetailsLoading,
    isRowsLoading: productDetailsLoading || productSummaryData.isFetching,

    selectedTab,
    onSelectTab: setSelectedTab,

    salesUnitOptions,
    defaultSalesUnit,
    onChangeSalesUnit,

    productIsAverage,
    productIsAverageResult,

    filteredSalesUnit,

    productSummaryDropdownData,
    isProductSummaryHistoryLoading,

    productId,
    selectedUnitSize,
    selectedUnitType,

    onClose: () => setVisibleModal(false),
    onEdit: navigateToEdit
  };
};
